/* ─────────────────────────────────────────────────────────────
   DGT Opticians · Shopify Theme JS
   ───────────────────────────────────────────────────────────── */

'use strict';

/* ── Utility ─────────────────────────────────────────────────── */
const $ = (sel, ctx = document) => ctx.querySelector(sel);
const $$ = (sel, ctx = document) => [...ctx.querySelectorAll(sel)];
const on = (el, ev, fn) => el && el.addEventListener(ev, fn);
const off = (el, ev, fn) => el && el.removeEventListener(ev, fn);

function formatMoney(amount, inCents = true) {
  const num = typeof amount === 'string' ? parseFloat(amount) : amount;
  if (num == null || Number.isNaN(num)) return '';
  const value = inCents ? num / 100 : num;
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(value);
}

/* ── Scroll reveal ───────────────────────────────────────────── */
function initReveal() {
  const els = $$('.reveal:not(.in)');
  if (!els.length) return;

  // No IO support — show everything immediately
  if (!('IntersectionObserver' in window)) {
    els.forEach(el => el.classList.add('in'));
    return;
  }

  // threshold: 0 — any visible pixel counts.
  // threshold: 0.1 fails on tall blocks (e.g. About Us): if the element is
  // taller than ~10× the viewport, intersectionRatio can never reach 0.1,
  // so content stays opacity:0 until a scroll forces a re-check.
  const obs = new IntersectionObserver((entries) => {
    entries.forEach(e => {
      if (e.isIntersecting || e.intersectionRatio > 0) {
        e.target.classList.add('in');
        obs.unobserve(e.target);
      }
    });
  }, { threshold: 0, rootMargin: '0px 0px -40px 0px' });

  els.forEach(el => {
    const rect = el.getBoundingClientRect();
    // Already in (or crossing) the viewport — reveal now, don't wait for IO
    if (rect.top < window.innerHeight && rect.bottom > 0) {
      el.classList.add('in');
    } else {
      obs.observe(el);
    }
  });
}

/* ── Header scroll ───────────────────────────────────────────── */
function initHeader() {
  const header = $('.site-header');
  if (!header) return;
  const onScroll = () => header.classList.toggle('scrolled', window.scrollY > 8);
  onScroll();
  on(window, 'scroll', onScroll, { passive: true });
}

/* ── Mega menu / nav dropdown ─────────────────────────────────── */
function initMegaMenu() {
  const header = $('.site-header');
  if (!header) return;
  let closeTimer = null;

  function closeAll() {
    $$('[data-mega-panel]', header).forEach(p => p.classList.remove('open'));
    $$('[data-mega-trigger]', header).forEach(b => b.setAttribute('aria-expanded', 'false'));
  }

  function scheduleClose() {
    clearTimeout(closeTimer);
    closeTimer = setTimeout(closeAll, 120);
  }

  function openMenu(btn, menu) {
    clearTimeout(closeTimer);
    closeAll();
    menu.classList.add('open');
    btn.setAttribute('aria-expanded', 'true');
  }

  $$('[data-mega-trigger]', header).forEach(btn => {
    const key = btn.dataset.megaTrigger;
    const menu = $(`[data-mega-panel="${key}"]`);
    if (!menu) return;
    const item = btn.closest('.header-nav-item');

    on(btn, 'mouseenter', () => openMenu(btn, menu));
    on(btn, 'focus', () => openMenu(btn, menu));

    if (item) {
      on(item, 'mouseleave', scheduleClose);
      on(item, 'mouseenter', () => {
        clearTimeout(closeTimer);
        openMenu(btn, menu);
      });
    } else {
      on(btn, 'mouseleave', scheduleClose);
      on(menu, 'mouseenter', () => clearTimeout(closeTimer));
      on(menu, 'mouseleave', scheduleClose);
    }
  });

  $$('.header-nav .navlink:not([data-mega-trigger])', header).forEach(link => {
    on(link, 'mouseenter', () => {
      clearTimeout(closeTimer);
      closeAll();
    });
  });

  on(header, 'mouseleave', scheduleClose);

  on(document, 'keydown', e => {
    if (e.key === 'Escape') closeAll();
  });
}

/* ── Mobile menu ─────────────────────────────────────────────── */
function initMobileMenu() {
  const openBtn = $('[data-mobile-menu-open]');
  const closeBtn = $('[data-mobile-menu-close]');
  const menu = $('.mobile-menu');
  if (!openBtn || !menu) return;

  openBtn.addEventListener('click', () => {
    menu.classList.add('open');
    document.body.style.overflow = 'hidden';
    openBtn.setAttribute('aria-expanded', 'true');
  });

  if (closeBtn) closeBtn.addEventListener('click', closeMobileMenu);

  function closeMobileMenu() {
    menu.classList.remove('open');
    document.body.style.overflow = '';
    openBtn.setAttribute('aria-expanded', 'false');
  }

  on(document, 'keydown', e => { if (e.key === 'Escape') closeMobileMenu(); });
}

/* ── Cart drawer ─────────────────────────────────────────────── */
const dgtBrandCache = new Map();

function dgtCacheBrandsFromDom() {
  $$('.product-card').forEach(card => {
    const link = card.querySelector('a[href*="/products/"]');
    const brand = card.querySelector('.product-card-brand')?.textContent?.trim();
    if (!link || !brand) return;
    const handle = link.pathname.split('/products/')[1]?.split('/')[0]?.split('?')[0];
    if (handle) dgtBrandCache.set(decodeURIComponent(handle), brand);
  });
}

function dgtIsStoreVendor(name) {
  const n = String(name || '').trim().toLowerCase();
  if (!n) return true;
  return n === 'dgt opticians' || n === 'dgt' || n.includes('dgt optic');
}

function dgtBrandFromItem(item) {
  if (item.handle && dgtBrandCache.has(item.handle)) return dgtBrandCache.get(item.handle);
  const fromProps = item.properties && (item.properties._brand || item.properties.Brand);
  if (fromProps && !dgtIsStoreVendor(fromProps)) return fromProps;
  if (item.vendor && !dgtIsStoreVendor(item.vendor)) return item.vendor;
  return '';
}

async function dgtResolveBrand(item) {
  const cached = dgtBrandFromItem(item);
  if (cached) return cached;
  if (!item.handle) return '';
  const handle = decodeURIComponent(item.handle);

  // Prefer product.brand template — returns custom.brand metafield, not store vendor
  try {
    const res = await fetch(`/products/${encodeURIComponent(handle)}?view=brand`, {
      credentials: 'same-origin',
      cache: 'force-cache'
    });
    if (res.ok) {
      const brand = (await res.text()).trim();
      if (brand && !dgtIsStoreVendor(brand)) {
        dgtBrandCache.set(handle, brand);
        return brand;
      }
    }
  } catch {
    /* fall through */
  }

  try {
    const res = await fetch(`/products/${encodeURIComponent(handle)}.js`, {
      credentials: 'same-origin',
      cache: 'force-cache'
    });
    if (!res.ok) return '';
    const product = await res.json();
    const brand = (product.vendor || '').trim();
    if (brand && !dgtIsStoreVendor(brand)) {
      dgtBrandCache.set(handle, brand);
      return brand;
    }
  } catch {
    /* ignore */
  }
  return '';
}

function dgtEscapeAttr(value) {
  return String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/"/g, '&quot;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
}

function dgtCartItemImage(item) {
  if (!item.image) return '';
  // cart.js may return protocol-relative or bare CDN paths
  if (item.image.startsWith('//')) return `https:${item.image}`;
  return item.image;
}

class CartDrawer {
  constructor() {
    this.drawer = $('.cart-drawer');
    this.overlay = $('.cart-overlay');
    this.itemsEl = $('.cart-items');
    this.subtotalEl = $('[data-cart-subtotal]');
    this.totalEl = $('[data-cart-total]');
    this.titleEl = $('[data-cart-title]');
    this._fetchSeq = 0;
    this._updating = false;
    if (!this.drawer) return;
    dgtCacheBrandsFromDom();
    this.bind();
    this.fetchAndRender();
  }

  bind() {
    $$('[data-open-cart]').forEach(btn => on(btn, 'click', () => this.open()));
    on(this.overlay, 'click', () => this.close());
    on($('[data-cart-close]'), 'click', () => this.close());
    on(document, 'keydown', e => { if (e.key === 'Escape') this.close(); });

    // Delegated events inside drawer
    on(this.itemsEl, 'click', e => {
      const removeBtn = e.target.closest('[data-remove-item]');
      const qtyBtn = e.target.closest('[data-qty-btn]');
      if (removeBtn) {
        e.preventDefault();
        this.removeItem(removeBtn.dataset.removeItem);
      }
      if (qtyBtn) {
        e.preventDefault();
        const key = qtyBtn.dataset.qtyBtn;
        const delta = qtyBtn.dataset.delta === '+' ? 1 : -1;
        this.changeQty(key, delta);
      }
    });
  }

  open() {
    this.fetchAndRender();
    this.drawer.classList.add('open');
    this.overlay.classList.add('open');
    document.body.style.overflow = 'hidden';
    $('[data-open-cart]') && $('[data-open-cart]').setAttribute('aria-expanded', 'true');
  }

  close() {
    this.drawer.classList.remove('open');
    this.overlay.classList.remove('open');
    document.body.style.overflow = '';
    $('[data-open-cart]') && $('[data-open-cart]').setAttribute('aria-expanded', 'false');
  }

  async fetchAndRender() {
    const seq = ++this._fetchSeq;
    try {
      const res = await fetch('/cart.js', { credentials: 'same-origin', cache: 'no-store' });
      if (!res.ok) throw new Error(`Cart fetch failed (${res.status})`);
      const cart = await res.json();
      if (seq !== this._fetchSeq) return cart; // newer request won
      this.render(cart);
      this.updateCount(cart.item_count);
      return cart;
    } catch (err) {
      console.error('Cart fetch error:', err);
      return null;
    }
  }

  render(cart) {
    if (!this.itemsEl) return;
    if (cart.item_count === 0) {
      this.itemsEl.innerHTML = `
        <div class="cart-empty">
          <div class="cart-empty-title">Your bag is empty</div>
          <p>Browse the new arrivals to begin.</p>
          <a href="/collections/all" class="btn btn--solid" style="margin-top:24px">Shop Now</a>
        </div>`;
    } else {
      dgtCacheBrandsFromDom();
      // Render immediately from cart.js — never block on brand network calls
      this.itemsEl.innerHTML = cart.items.map(item => {
        const brand = dgtBrandFromItem(item);
        const img = dgtCartItemImage(item);
        const key = dgtEscapeAttr(item.key);
        const title = dgtEscapeAttr(item.product_title);
        const meta = item.variant_title && item.variant_title !== 'Default Title'
          ? dgtEscapeAttr(item.variant_title)
          : '';
        return `
        <div class="cart-item" data-key="${key}">
          <div class="cart-item-img">
            <a href="${dgtEscapeAttr(item.url)}">
              ${img ? `<img src="${dgtEscapeAttr(img)}" alt="${title}" loading="lazy" width="200" height="200">` : ''}
            </a>
          </div>
          <div>
            <div class="cart-item-brand" data-cart-brand>${dgtEscapeAttr(brand)}</div>
            <div class="cart-item-title"><a href="${dgtEscapeAttr(item.url)}">${title}</a></div>
            <div class="cart-item-meta">${meta}</div>
            <div class="cart-item-footer">
              <div class="qty-control">
                <button type="button" class="qty-btn" data-qty-btn="${key}" data-delta="-" aria-label="Decrease quantity">−</button>
                <span class="qty-value">${item.quantity}</span>
                <button type="button" class="qty-btn" data-qty-btn="${key}" data-delta="+" aria-label="Increase quantity">+</button>
              </div>
              <div class="cart-item-price">${formatMoney(item.final_line_price ?? item.line_price)}</div>
            </div>
            <button type="button" class="cart-item-remove" data-remove-item="${key}">Remove</button>
          </div>
        </div>`;
      }).join('');

      // Fill missing brands in the background without delaying qty/totals
      cart.items.forEach(item => {
        if (dgtBrandFromItem(item)) return;
        dgtResolveBrand(item).then(brand => {
          if (!brand || !this.itemsEl) return;
          const row = this.itemsEl.querySelector(`[data-key="${CSS.escape(item.key)}"] [data-cart-brand]`);
          if (row && !row.textContent.trim()) row.textContent = brand;
        });
      });
    }

    if (this.subtotalEl) this.subtotalEl.textContent = formatMoney(cart.total_price);
    if (this.totalEl) this.totalEl.textContent = formatMoney(cart.total_price);

    const checkoutBtn = $('[data-cart-checkout]');
    if (checkoutBtn) checkoutBtn.disabled = cart.item_count === 0;
  }

  updateCount(count) {
    const n = Number(count) || 0;
    // Re-query so header badge always stays in sync (not just constructor cache)
    $$('[data-cart-count]').forEach(el => {
      el.textContent = n;
      el.classList.toggle('has-items', n > 0);
    });
    const titleEl = this.titleEl || $('[data-cart-title]');
    if (titleEl) {
      titleEl.textContent = `${n} ${n === 1 ? 'item' : 'items'}`;
    }
  }

  async removeItem(key) {
    await this.changeLine(key, 0);
  }

  async changeQty(key, delta) {
    if (this._updating || !key) return;
    const item = this.itemsEl && this.itemsEl.querySelector(`[data-key="${CSS.escape(key)}"]`);
    if (!item) return;
    const current = parseInt(item.querySelector('.qty-value')?.textContent, 10);
    if (Number.isNaN(current)) return;
    const newQty = Math.max(0, current + delta);
    // Optimistic UI so qty feels instant
    const qtyEl = item.querySelector('.qty-value');
    if (qtyEl) qtyEl.textContent = String(newQty);
    await this.changeLine(key, newQty);
  }

  async changeLine(key, quantity) {
    if (this._updating) return;
    this._updating = true;
    try {
      const res = await fetch('/cart/change.js', {
        method: 'POST',
        credentials: 'same-origin',
        cache: 'no-store',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ id: key, quantity }),
      });
      if (!res.ok) throw new Error(`Cart change failed (${res.status})`);
      const cart = await res.json();
      this.render(cart);
      this.updateCount(cart.item_count);
    } catch (err) {
      console.error('Cart update error:', err);
      // Re-sync from server if optimistic update failed
      await this.fetchAndRender();
    } finally {
      this._updating = false;
    }
  }
}

/* ── Add to cart ─────────────────────────────────────────────── */
async function addToCart(variantId, qty = 1, properties) {
  const payload = { id: Number(variantId), quantity: qty };
  if (properties && Object.keys(properties).length) payload.properties = properties;
  const res = await fetch('/cart/add.js', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  if (!res.ok) throw new Error('Add to cart failed');
  return res.json();
}

function initAddToCart() {
  on(document, 'click', async e => {
    const btn = e.target.closest('[data-add-to-cart]');
    if (!btn) return;
    const variantId = btn.dataset.variantId || btn.closest('[data-variant-id]')?.dataset.variantId;
    if (!variantId) return;

    const card = btn.closest('.product-card');
    let brand = card?.querySelector('.product-card-brand')?.textContent?.trim()
      || btn.dataset.productBrand
      || '';
    if (!brand) {
      const vendorEl = document.querySelector('.product-vendor');
      if (vendorEl) brand = vendorEl.textContent.split('·')[0].trim();
    }
    const link = card?.querySelector('a[href*="/products/"]');
    const handle = link?.pathname.split('/products/')[1]?.split('/')[0]?.split('?')[0]
      || btn.dataset.productHandle
      || window.location.pathname.split('/products/')[1]?.split('/')[0]?.split('?')[0];
    if (handle && brand) dgtBrandCache.set(decodeURIComponent(handle), brand);

    btn.disabled = true;
    const originalText = btn.textContent;
    btn.textContent = 'Adding…';

    try {
      await addToCart(variantId, 1, brand ? { _brand: brand } : undefined);
      btn.textContent = 'Added ✓';
      setTimeout(() => { btn.textContent = originalText; btn.disabled = false; }, 2000);

      // Open cart drawer with a fresh cart snapshot
      if (window.dgtCart) window.dgtCart.open();
    } catch {
      btn.textContent = 'Error';
      setTimeout(() => { btn.textContent = originalText; btn.disabled = false; }, 2000);
    }
  });
}

/* ── Product page (form, gallery, tabs) ──────────────────────── */
function initProductForm() {
  const productRoot = $('.product-page-main');
  if (!productRoot) return;

  const form = $('[data-product-form]', productRoot);

  // Variant selects / swatches (swatches live outside the form in main-product.liquid)
  $$('[data-swatch]', productRoot).forEach(sw => {
    on(sw, 'click', () => {
      $$('[data-swatch]', productRoot).forEach(s => s.classList.remove('active'));
      sw.classList.add('active');

      if (form) {
        const variantId = sw.dataset.variantId;
        const hiddenInput = $('[name="id"]', form);
        if (hiddenInput) hiddenInput.value = variantId;
        form.dataset.variantId = variantId;

        const price = sw.dataset.price;
        const addBtn = $('[data-add-to-cart]', form);
        const available = sw.dataset.available === 'true';
        if (addBtn) {
          addBtn.disabled = !available;
          if (available && price) addBtn.textContent = `Add to Bag · ${formatMoney(parseInt(price))}`;
          else if (!available) addBtn.textContent = 'Sold Out';
        }

        const priceEl = $('[data-product-price]');
        if (priceEl && price) priceEl.textContent = formatMoney(parseInt(price));
      }

      // Update image
      const imgSrc = sw.dataset.image;
      if (imgSrc) {
        const mainImg = $('[data-main-product-img]');
        if (mainImg) mainImg.src = imgSrc;
      }

      // Update colour tag
      const colourTag = $('[data-product-colour-tag]');
      if (colourTag && sw.dataset.variantTitle) {
        const title = sw.dataset.variantTitle;
        if (title && title !== 'Default Title') {
          colourTag.textContent = title.replace(/ \/ /g, ' · ').toUpperCase();
          colourTag.style.display = '';
        } else {
          colourTag.style.display = 'none';
        }
      }
    });
  });

  // Gallery thumbnails & arrows
  const thumbs = $$('[data-product-thumb]');
  if (thumbs.length) {
    const mainImg = $('[data-main-product-img]');
    const counter = $('[data-img-counter]');
    const total = thumbs.length;

    function setGalleryIndex(index) {
      const i = Math.max(0, Math.min(index, total - 1));
      const thumb = thumbs[i];
      thumbs.forEach(t => t.classList.remove('active'));
      thumb.classList.add('active');
      if (mainImg) mainImg.src = thumb.dataset.src;
      if (counter) counter.textContent = `${i + 1} / ${total} · click to zoom`;
      thumb.scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: 'smooth' });
      updateGalleryNavState(i);
    }

    function getGalleryIndex() {
      const activeIdx = thumbs.findIndex(t => t.classList.contains('active'));
      return activeIdx >= 0 ? activeIdx : 0;
    }

    function updateGalleryNavState(index) {
      const atStart = index <= 0;
      const atEnd = index >= total - 1;
      if (prevBtn) {
        prevBtn.disabled = atStart;
        prevBtn.classList.toggle('is-disabled', atStart);
        prevBtn.setAttribute('aria-disabled', atStart ? 'true' : 'false');
      }
      if (nextBtn) {
        nextBtn.disabled = atEnd;
        nextBtn.classList.toggle('is-disabled', atEnd);
        nextBtn.setAttribute('aria-disabled', atEnd ? 'true' : 'false');
      }
    }

    thumbs.forEach((thumb, i) => {
      on(thumb, 'click', () => setGalleryIndex(i));
    });

    const prevBtn = $('[data-product-gallery-prev]');
    const nextBtn = $('[data-product-gallery-next]');
    if (prevBtn) {
      on(prevBtn, 'click', e => {
        e.stopPropagation();
        if (prevBtn.disabled) return;
        setGalleryIndex(getGalleryIndex() - 1);
      });
    }
    if (nextBtn) {
      on(nextBtn, 'click', e => {
        e.stopPropagation();
        if (nextBtn.disabled) return;
        setGalleryIndex(getGalleryIndex() + 1);
      });
    }

    updateGalleryNavState(getGalleryIndex());
  }

  // Zoom
  const mainWrap = $('[data-main-product-img-wrap]');
  const zoomOverlay = $('.zoom-overlay');
  if (mainWrap && zoomOverlay) {
    const zoomImg = $('img', zoomOverlay);
    on(mainWrap, 'click', e => {
      if (e.target.closest('[data-product-gallery-prev], [data-product-gallery-next]')) return;
      const src = $('[data-main-product-img]')?.src;
      if (src && zoomImg) zoomImg.src = src;
      zoomOverlay.classList.add('open');
    });
    on(zoomOverlay, 'click', () => zoomOverlay.classList.remove('open'));
    on(document, 'keydown', e => { if (e.key === 'Escape') zoomOverlay.classList.remove('open'); });
  }

  // Product tabs
  $$('[data-tab-btn]').forEach(btn => {
    on(btn, 'click', () => {
      $$('[data-tab-btn]').forEach(b => b.classList.remove('active'));
      $$('[data-tab-panel]').forEach(p => p.classList.remove('active'));
      btn.classList.add('active');
      const panel = $(`[data-tab-panel="${btn.dataset.tabBtn}"]`);
      if (panel) panel.classList.add('active');
    });
  });
}

/* ── Search ──────────────────────────────────────────────────── */
function initSearch() {
  const overlay = $('.search-overlay');
  const input = $('.search-input');
  const resultsEl = $('.search-results');
  if (!overlay || !resultsEl) return;

  let suggestedHTML = resultsEl.innerHTML;

  async function fillSearchBrands(root) {
    dgtCacheBrandsFromDom();
    const rows = $$('.search-result', root || resultsEl);
    await Promise.all(rows.map(async row => {
      const brandEl = $('.search-result-brand', row);
      if (!brandEl) return;
      const href = row.getAttribute('href') || '';
      const handle = decodeURIComponent(
        href.split('/products/')[1]?.split('/')[0]?.split('?')[0] || ''
      );
      if (!handle) return;

      const existing = brandEl.textContent.trim();
      if (existing && !dgtIsStoreVendor(existing)) {
        dgtBrandCache.set(handle, existing);
        return;
      }

      const brand = await dgtResolveBrand({ handle, properties: {} });
      if (brand) brandEl.textContent = brand;
      else if (dgtIsStoreVendor(existing)) brandEl.textContent = '';
    }));
  }

  async function captureSuggested() {
    await fillSearchBrands(resultsEl);
    suggestedHTML = resultsEl.innerHTML;
  }

  // Resolve brands on load (header collections.all often omits metafields)
  captureSuggested();

  $$('[data-open-search]').forEach(btn => {
    on(btn, 'click', () => {
      overlay.classList.add('open');
      document.body.style.overflow = 'hidden';
      fillSearchBrands(resultsEl);
      setTimeout(() => input && input.focus(), 50);
    });
  });

  const closeBtns = $$('[data-close-search]');
  closeBtns.forEach(btn => on(btn, 'click', closeSearch));
  on($('.search-bg'), 'click', closeSearch);

  function closeSearch() {
    overlay.classList.remove('open');
    document.body.style.overflow = '';
    if (input) input.value = '';
    renderSearchResults('');
  }

  on(document, 'keydown', e => { if (e.key === 'Escape' && overlay.classList.contains('open')) closeSearch(); });

  let debounce = null;
  on(input, 'input', () => {
    clearTimeout(debounce);
    debounce = setTimeout(() => doSearch(input.value.trim()), 280);
  });

  async function doSearch(q) {
    if (!q) { renderSearchResults(''); return; }
    try {
      const res = await fetch(`/search?q=${encodeURIComponent(q)}&type=product&view=suggest`);
      const products = await res.json();
      await renderSearchResults(q, Array.isArray(products) ? products : []);
    } catch {
      await renderSearchResults(q, null);
    }
  }

  async function renderSearchResults(q, products) {
    if (!resultsEl) return;
    if (!q) {
      resultsEl.innerHTML = suggestedHTML || '<div class="search-no-results">Try a brand name, frame style or model number.</div>';
      await fillSearchBrands(resultsEl);
      return;
    }
    if (!products || !products.length) {
      resultsEl.innerHTML = '<div class="search-no-results">No matches — try a brand or shape.</div>';
      return;
    }
    resultsEl.innerHTML = products.map(p => {
      const handle = decodeURIComponent((p.url || '').split('/products/')[1]?.split('?')[0] || '');
      const brand = (p.brand && !dgtIsStoreVendor(p.brand)) ? p.brand : (dgtBrandCache.get(handle) || '');
      if (handle && brand) dgtBrandCache.set(handle, brand);
      return `
      <a class="search-result" href="${p.url}" data-product-handle="${handle}">
        <div class="search-result-thumb">
          ${p.featured_image?.url ? `<img src="${p.featured_image.url}" alt="${dgtEscapeAttr(p.title)}" loading="lazy">` : ''}
        </div>
        <div>
          <div class="search-result-brand">${dgtEscapeAttr(brand)}</div>
          <div class="search-result-title">${dgtEscapeAttr(p.title)}</div>
        </div>
        ${Number(p.price) > 0 ? `<div class="search-result-price">${formatMoney(p.price, false)}</div>` : ''}
      </a>`;
    }).join('');
    await fillSearchBrands(resultsEl);
  }
}

/* ── Testimonials rotator ────────────────────────────────────── */
function initTestimonials() {
  const section = $('.testimonials-section');
  if (!section) return;
  const texts = $$('[data-testimonial-text]', section);
  const names = $$('[data-testimonial-name]', section);
  const locs = $$('[data-testimonial-loc]', section);
  const dots = $$('.testimonial-dot', section);
  const quotes = JSON.parse(section.dataset.testimonials || '[]');
  if (quotes.length < 2) return;

  let idx = 0;
  const textEl = $('[data-testimonial-quote]', section);
  const nameEl = $('[data-testimonial-author-name]', section);
  const locEl = $('[data-testimonial-author-loc]', section);

  function show(i) {
    idx = i;
    if (textEl) { textEl.classList.remove('fade-in'); void textEl.offsetWidth; textEl.classList.add('fade-in'); textEl.textContent = quotes[i].quote; }
    if (nameEl) nameEl.textContent = quotes[i].name;
    if (locEl) locEl.textContent = quotes[i].loc;
    dots.forEach((d, di) => d.classList.toggle('active', di === i));
  }

  dots.forEach((dot, di) => on(dot, 'click', () => { clearInterval(timer); show(di); }));

  let timer = setInterval(() => show((idx + 1) % quotes.length), 7000);
}

/* ── Collection: storefront filters via section AJAX + New Arrivals ─ */
const DGT_COLLECTION_SECTION_FALLBACK = 'collection-content';

let dgtCollectionFetchAbort;

function dgtGetCollectionSectionId() {
  const root = document.getElementById('collection-ajax-root');
  return root?.dataset?.sectionId || DGT_COLLECTION_SECTION_FALLBACK;
}

function dgtExtractSectionHtml(data, sectionId) {
  if (!data || typeof data !== 'object') return null;
  const html = data[sectionId] ?? data.sections?.[sectionId];
  if (html) return html;
  const keys = Object.keys(data).filter(k => k !== 'sections' && data[k]);
  const bySuffix = keys.find(k => k === sectionId || k.endsWith(`__${DGT_COLLECTION_SECTION_FALLBACK}`));
  if (bySuffix) return data[bySuffix];
  if (keys.length === 1) return data[keys[0]];
  return null;
}

function dgtParseCollectionRootInner(html) {
  const parsed = new DOMParser().parseFromString(html, 'text/html');
  const nextRoot = parsed.getElementById('collection-ajax-root');
  if (nextRoot) {
    return { inner: nextRoot.innerHTML, sectionId: nextRoot.dataset.sectionId };
  }
  const wrapper = parsed.querySelector('.shopify-section');
  if (wrapper) {
    const nested = wrapper.querySelector('#collection-ajax-root');
    if (nested) return { inner: nested.innerHTML, sectionId: nested.dataset.sectionId };
    return { inner: wrapper.innerHTML, sectionId: null };
  }
  return null;
}

function dgtBuildUrlFromCollectionForm(form) {
  const fd = new FormData(form);
  const params = new URLSearchParams();
  for (const [key, val] of fd.entries()) {
    if (val === '' || val == null) continue;
    params.append(key, val);
  }
  const u = new URL(window.location.pathname, window.location.origin);
  const qs = params.toString();
  if (qs) u.search = qs;
  return u;
}

async function dgtFetchCollectionRootInner(pageUrl, sectionId, { signal } = {}) {
  const sectionUrl = new URL(pageUrl.pathname + pageUrl.search, window.location.origin);
  sectionUrl.searchParams.set('sections', sectionId);

  const sectionRes = await fetch(sectionUrl.toString(), {
    headers: { 'X-Requested-With': 'XMLHttpRequest', Accept: 'application/json' },
    credentials: 'same-origin',
    signal,
  });

  if (sectionRes.ok) {
    const ct = sectionRes.headers.get('content-type') || '';
    if (ct.includes('application/json')) {
      const data = await sectionRes.json();
      const html = dgtExtractSectionHtml(data, sectionId);
      if (html) {
        const parsed = dgtParseCollectionRootInner(html);
        if (parsed) return parsed;
      }
    }
  }

  const pageRes = await fetch(pageUrl.toString(), {
    headers: { 'X-Requested-With': 'XMLHttpRequest' },
    credentials: 'same-origin',
    signal,
  });
  if (!pageRes.ok) throw new Error('Collection page fetch failed');
  const parsed = dgtParseCollectionRootInner(await pageRes.text());
  if (!parsed) throw new Error('Missing collection-ajax-root');
  return parsed;
}

async function dgtSwapCollectionSection(visibleUrl, { pushHistory } = { pushHistory: true }) {
  const root = document.getElementById('collection-ajax-root');
  if (!root) return false;

  if (dgtCollectionFetchAbort) dgtCollectionFetchAbort.abort();
  dgtCollectionFetchAbort = new AbortController();
  const signal = dgtCollectionFetchAbort.signal;
  dgtInfiniteLoading = false;
  if (dgtInfiniteObserver) {
    dgtInfiniteObserver.disconnect();
    dgtInfiniteObserver = null;
  }

  const sectionId = dgtGetCollectionSectionId();
  const u = visibleUrl instanceof URL ? new URL(visibleUrl.href) : new URL(visibleUrl, window.location.origin);
  u.searchParams.delete('page');
  const pageUrl = new URL(u.pathname + u.search, window.location.origin);

  root.classList.add('collection-ajax-root--loading');
  try {
    const { inner, sectionId: nextSectionId } = await dgtFetchCollectionRootInner(pageUrl, sectionId, { signal });

    root.innerHTML = inner;
    if (nextSectionId) root.dataset.sectionId = nextSectionId;

    const countSrc = root.querySelector('[data-dgt-product-count]');
    const heroStat = document.querySelector('.collection-hero .hero-stat-num');
    if (countSrc && heroStat && countSrc.dataset.dgtProductCount != null) {
      heroStat.textContent = countSrc.dataset.dgtProductCount;
    }

    dgtSyncCollectionFacetInputs();
    dgtCloseCollectionFiltersDrawer();
    initCollectionInfiniteScroll();

    if (pushHistory) {
      history.pushState({ dgtCollectionAjax: true }, '', pageUrl.pathname + pageUrl.search);
    }
    initReveal();
    return true;
  } catch (err) {
    if (err.name === 'AbortError') return false;
    console.error('Collection AJAX filter failed:', err);
    window.location.assign(pageUrl.pathname + pageUrl.search);
    return false;
  } finally {
    root.classList.remove('collection-ajax-root--loading');
  }
}

function dgtIsCollectionMobileFilters() {
  return window.matchMedia('(max-width: 900px)').matches;
}

function dgtCloseCollectionFiltersDrawer() {
  const drawer = document.querySelector('.collection-filters-drawer');
  const overlay = document.querySelector('.collection-filters-overlay');
  if (drawer) {
    drawer.classList.remove('open');
    drawer.setAttribute('aria-hidden', 'true');
  }
  if (overlay) {
    overlay.classList.remove('open');
    overlay.setAttribute('aria-hidden', 'true');
  }
  document.body.style.overflow = '';
  $$('[data-collection-drawer-open]').forEach(btn => btn.setAttribute('aria-expanded', 'false'));
}

function dgtOpenCollectionFiltersDrawer(panel) {
  const drawer = document.querySelector('.collection-filters-drawer');
  const overlay = document.querySelector('.collection-filters-overlay');
  if (!drawer || !overlay) return;

  drawer.querySelectorAll('[data-collection-drawer-panel]').forEach(p => {
    p.hidden = p.dataset.collectionDrawerPanel !== panel;
  });

  const footer = drawer.querySelector('[data-collection-drawer-footer]');
  if (footer) footer.hidden = panel !== 'filter';

  const title = drawer.querySelector('[data-collection-drawer-title]');
  if (title) {
    title.textContent = panel === 'sort'
      ? (title.dataset.titleSort || 'Sort by')
      : (title.dataset.titleFilter || 'Filter');
  }

  overlay.classList.add('open');
  overlay.setAttribute('aria-hidden', 'false');
  drawer.classList.add('open');
  drawer.setAttribute('aria-hidden', 'false');
  document.body.style.overflow = 'hidden';

  $$('[data-collection-drawer-open]').forEach(btn => {
    btn.setAttribute('aria-expanded', btn.dataset.collectionDrawerOpen === panel ? 'true' : 'false');
  });
}

function dgtSyncCollectionFacetInputs() {
  const form = document.getElementById('CollectionFiltersForm');
  if (!form) return;
  const isMobile = dgtIsCollectionMobileFilters();

  form.querySelectorAll('.collection-facets-desktop input, .collection-facets-desktop button.collection-facet-price-apply').forEach(el => {
    el.disabled = isMobile;
  });
  form.querySelectorAll('.collection-filters-drawer input').forEach(el => {
    el.disabled = !isMobile;
  });
}

function dgtBindCollectionAjaxDocumentListeners() {
  if (window.dgtCollectionAjaxDocBound) return;
  window.dgtCollectionAjaxDocBound = true;

  document.addEventListener('submit', e => {
    const form = e.target;
    if (!(form instanceof HTMLFormElement) || form.id !== 'CollectionFiltersForm') return;
    e.preventDefault();
    e.stopImmediatePropagation();
    void dgtSwapCollectionSection(dgtBuildUrlFromCollectionForm(form), { pushHistory: true });
  }, true);

  document.addEventListener('change', e => {
    const t = e.target;
    if (!t.matches('#CollectionFiltersForm input[type="checkbox"], #CollectionFiltersForm select[name="sort_by"]')) return;
    const form = t.closest('#CollectionFiltersForm');
    if (!form) return;
    if (t.matches('input[type="checkbox"]') && t.closest('.collection-filters-drawer') && dgtIsCollectionMobileFilters()) return;
    void dgtSwapCollectionSection(dgtBuildUrlFromCollectionForm(form), { pushHistory: true });
  }, true);

  document.addEventListener('click', e => {
    const sortOpt = e.target.closest('[data-sort-value]');
    if (sortOpt && sortOpt.closest('.collection-filters-drawer')) {
      e.preventDefault();
      const form = sortOpt.closest('#CollectionFiltersForm');
      const select = form && form.querySelector('#CollectionSort');
      if (select) {
        select.value = sortOpt.dataset.sortValue;
        void dgtSwapCollectionSection(dgtBuildUrlFromCollectionForm(form), { pushHistory: true });
      }
      return;
    }

    const openBtn = e.target.closest('[data-collection-drawer-open]');
    if (openBtn) {
      e.preventDefault();
      dgtOpenCollectionFiltersDrawer(openBtn.dataset.collectionDrawerOpen);
      return;
    }

    if (e.target.closest('[data-collection-drawer-close]') || e.target.closest('[data-collection-drawer-overlay]')) {
      e.preventDefault();
      dgtCloseCollectionFiltersDrawer();
      return;
    }

    const a = e.target.closest('a.collection-filter-ajax');
    if (!a) return;
    const root = document.getElementById('collection-ajax-root');
    if (!root || !root.contains(a)) return;
    if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || a.target === '_blank') return;
    e.preventDefault();
    e.stopImmediatePropagation();
    void dgtSwapCollectionSection(new URL(a.href), { pushHistory: true });
  }, true);

  document.addEventListener('keydown', e => {
    if (e.key === 'Escape' && document.querySelector('.collection-filters-drawer.open')) {
      dgtCloseCollectionFiltersDrawer();
    }
  });

  if (!window.dgtCollectionResizeBound) {
    window.dgtCollectionResizeBound = true;
    on(window, 'resize', dgtSyncCollectionFacetInputs, { passive: true });
  }

  if (!window.dgtCollectionPopstateBound) {
    window.dgtCollectionPopstateBound = true;
    on(window, 'popstate', () => {
      if (!document.getElementById('collection-ajax-root')) return;
      void dgtSwapCollectionSection(new URL(window.location.href), { pushHistory: false });
    });
  }
}

function dgtBindCollectionFacetAccordion() {
  if (window.dgtCollectionFacetAccordionBound) return;
  window.dgtCollectionFacetAccordionBound = true;

  document.addEventListener('toggle', e => {
    const details = e.target;
    if (!(details instanceof HTMLDetailsElement) || !details.classList.contains('collection-facet')) return;
    if (!details.open) return;
    const group = details.closest('.collection-facets-groups');
    if (!group) return;
    group.querySelectorAll('.collection-facet[open]').forEach(facet => {
      if (facet !== details) facet.open = false;
    });
  }, true);
}

function initCollectionPageAjax() {
  dgtBindCollectionAjaxDocumentListeners();
  dgtBindCollectionFacetAccordion();
  dgtSyncCollectionFacetInputs();
  dgtStripCollectionPageFromUrl();
  initCollectionInfiniteScroll();
}

function dgtStripCollectionPageFromUrl() {
  const u = new URL(window.location.href);
  if (!u.searchParams.has('page')) return;
  u.searchParams.delete('page');
  const clean = u.pathname + (u.searchParams.toString() ? `?${u.searchParams}` : '');
  history.replaceState({ dgtCollectionAjax: true }, '', clean);
}

let dgtInfiniteObserver = null;
let dgtInfiniteLoading = false;

function initCollectionInfiniteScroll() {
  if (dgtInfiniteObserver) {
    dgtInfiniteObserver.disconnect();
    dgtInfiniteObserver = null;
  }

  const root = document.getElementById('collection-ajax-root');
  if (!root) return;

  const prevTrigger = root.querySelector('[data-collection-infinite-prev]');
  const nextTrigger = root.querySelector('[data-collection-infinite-next]');
  if (!prevTrigger && !nextTrigger) return;

  dgtInfiniteObserver = new IntersectionObserver(
    entries => {
      entries.forEach(entry => {
        if (!entry.isIntersecting) return;
        if (entry.target.hasAttribute('data-collection-infinite-next')) {
          void dgtLoadCollectionPage('next');
        } else if (entry.target.hasAttribute('data-collection-infinite-prev')) {
          void dgtLoadCollectionPage('prev');
        }
      });
    },
    { root: null, rootMargin: '600px 0px', threshold: 0 }
  );

  if (nextTrigger && nextTrigger.dataset.nextUrl) dgtInfiniteObserver.observe(nextTrigger);
  if (prevTrigger && prevTrigger.dataset.prevUrl) dgtInfiniteObserver.observe(prevTrigger);
}

async function dgtLoadCollectionPage(direction) {
  if (dgtInfiniteLoading) return;

  const root = document.getElementById('collection-ajax-root');
  const grid = root && root.querySelector('.products-grid');
  if (!grid) return;

  const isPrev = direction === 'prev';
  const trigger = root.querySelector(
    isPrev ? '[data-collection-infinite-prev]' : '[data-collection-infinite-next]'
  );
  if (!trigger) return;

  const pageHref = isPrev ? trigger.dataset.prevUrl : trigger.dataset.nextUrl;
  if (!pageHref) return;

  dgtInfiniteLoading = true;
  trigger.classList.add('is-loading');

  try {
    const sectionId = dgtGetCollectionSectionId();
    const pageUrl = new URL(pageHref, window.location.origin);
    const { inner } = await dgtFetchCollectionRootInner(pageUrl, sectionId);
    const doc = new DOMParser().parseFromString(inner, 'text/html');
    const remoteGrid = doc.querySelector('.products-grid');
    if (!remoteGrid) return;

    const cards = [...remoteGrid.querySelectorAll('.product-card')];
    const remotePrev = remoteGrid.querySelector('[data-collection-infinite-prev]');
    const remoteNext = remoteGrid.querySelector('[data-collection-infinite-next]');

    if (isPrev) {
      const anchor = trigger.nextElementSibling;
      const beforeHeight = grid.scrollHeight;
      const beforeTop = window.scrollY || document.documentElement.scrollTop;

      cards.forEach(card => {
        const node = document.importNode(card, true);
        if (anchor) grid.insertBefore(node, anchor);
        else grid.appendChild(node);
      });

      if (remotePrev && remotePrev.dataset.prevUrl) {
        trigger.dataset.prevUrl = remotePrev.dataset.prevUrl;
        trigger.classList.remove('is-loading');
      } else {
        trigger.remove();
      }

      const afterHeight = grid.scrollHeight;
      window.scrollTo(0, beforeTop + (afterHeight - beforeHeight));
    } else {
      cards.forEach(card => {
        grid.insertBefore(document.importNode(card, true), trigger);
      });

      if (remoteNext && remoteNext.dataset.nextUrl) {
        trigger.dataset.nextUrl = remoteNext.dataset.nextUrl;
        trigger.classList.remove('is-loading');
      } else {
        trigger.remove();
      }
    }

    // Keep loading next/prev pages without putting ?page= in the address bar
    initCollectionInfiniteScroll();
    initReveal();
  } catch (err) {
    console.error('Collection infinite scroll failed:', err);
    trigger.classList.remove('is-loading');
  } finally {
    dgtInfiniteLoading = false;
  }
}

function dgtBindNewArrivalsSectionFilters() {
  if (window.dgtNewArrivalsFilterBound) return;
  window.dgtNewArrivalsFilterBound = true;

  document.addEventListener('click', e => {
    const pill = e.target.closest('[data-filter-tab]');
    if (!pill) return;
    const section = pill.closest('.new-arrivals-section');
    if (!section) return;

    e.preventDefault();
    const panelId = pill.dataset.filterTab;
    if (!panelId) return;

    $$('[data-filter-tab]', section).forEach(p => {
      p.classList.remove('active');
      p.setAttribute('aria-selected', 'false');
    });
    pill.classList.add('active');
    pill.setAttribute('aria-selected', 'true');

    $$('[data-filter-panel]', section).forEach(panel => {
      const isActive = panel.dataset.filterPanel === panelId;
      panel.classList.toggle('is-active', isActive);
      panel.toggleAttribute('hidden', !isActive);
    });
  });
}

function initNewArrivalsSectionFilters() {
  dgtBindNewArrivalsSectionFilters();
}

function initCollectionFilters() {
  initCollectionPageAjax();
  initNewArrivalsSectionFilters();
}

function countDescriptionWords(text) {
  return text.trim().split(/\s+/).filter(Boolean).length;
}

function truncateDescriptionHtml(element, maxWords) {
  const working = element.cloneNode(true);
  let words = 0;
  let truncateNode = null;
  let truncateOffset = 0;

  const walk = node => {
    if (truncateNode) return;
    if (node.nodeType === Node.TEXT_NODE) {
      const text = node.textContent;
      for (const match of text.matchAll(/\S+/g)) {
        words += 1;
        if (words === maxWords) {
          truncateNode = node;
          truncateOffset = match.index + match[0].length;
          return;
        }
      }
    } else if (node.nodeType === Node.ELEMENT_NODE) {
      [...node.childNodes].forEach(walk);
    }
  };

  walk(working);
  if (!truncateNode) return null;

  truncateNode.textContent = truncateNode.textContent.slice(0, truncateOffset).replace(/\s+$/, '');

  let node = truncateNode;
  while (node && node !== working) {
    let sibling = node.nextSibling;
    while (sibling) {
      const next = sibling.nextSibling;
      sibling.remove();
      sibling = next;
    }
    node = node.parentNode;
  }

  return working.innerHTML;
}

function findDescriptionAppendTarget(container) {
  const walker = document.createTreeWalker(container, NodeFilter.SHOW_TEXT);
  let lastText = null;
  while (walker.nextNode()) lastText = walker.currentNode;
  return lastText ? lastText.parentNode : container;
}

function appendInlineDescriptionToggle(target, readMoreLabel, onToggle) {
  target.querySelectorAll('.collection-description__ellipsis, .collection-description__toggle').forEach(el => el.remove());

  const ellipsis = document.createElement('span');
  ellipsis.className = 'collection-description__ellipsis';
  ellipsis.textContent = '... ';

  const btn = createDescriptionToggle(readMoreLabel);
  on(btn, 'click', onToggle);

  target.appendChild(ellipsis);
  target.appendChild(btn);
}

function createDescriptionToggle(label) {
  const btn = document.createElement('button');
  btn.type = 'button';
  btn.className = 'collection-description__toggle';
  btn.textContent = label;
  return btn;
}

function appendReadLessToggle(content, readLessLabel, onToggle) {
  content.querySelectorAll('.collection-description__toggle').forEach(el => el.remove());

  const lastBlock = [...content.querySelectorAll('p, li, h2, h3, h4, h5, h6')].pop() || content.lastElementChild || content;
  const btn = createDescriptionToggle(readLessLabel);
  on(btn, 'click', onToggle);

  lastBlock.appendChild(document.createTextNode(' '));
  lastBlock.appendChild(btn);
}

function initCollectionDescriptionToggle() {
  $$('[data-collection-description]').forEach(block => {
    if (block.dataset.descriptionInit) return;
    block.dataset.descriptionInit = 'true';

    const content = $('[data-collection-description-content]', block);
    if (!content) return;

    const maxWords = parseInt(block.dataset.wordLimit, 10) || 100;
    const readMoreLabel = block.dataset.readMoreLabel || 'Read more';
    const readLessLabel = block.dataset.readLessLabel || 'Read less';
    const fullHtml = content.innerHTML;

    if (countDescriptionWords(content.textContent) <= maxWords) return;

    const collapse = () => {
      block.classList.remove('is-expanded');

      const temp = document.createElement('div');
      temp.innerHTML = fullHtml;
      const truncatedHtml = truncateDescriptionHtml(temp, maxWords);
      if (!truncatedHtml) return;

      content.innerHTML = truncatedHtml;
      appendInlineDescriptionToggle(
        findDescriptionAppendTarget(content),
        readMoreLabel,
        () => expand()
      );
    };

    const expand = () => {
      block.classList.add('is-expanded');
      content.innerHTML = fullHtml;
      appendReadLessToggle(content, readLessLabel, () => collapse());
    };

    collapse();
  });
}

/* ── Instagram section (grid tone placeholders) ──────────────── */
function initInstagramTones() {
  $$('[data-ig-tone]').forEach(el => {
    const tone = el.dataset.igTone;
    const tones = {
      tortoise: ['#7a4a2c', '#3a2418'],
      paper:    ['#e6dfd2', '#a89888'],
      jet:      ['#2a241d', '#1a1612'],
      crystal:  ['#d6cdbc', '#7a7060'],
      gold:     ['#c89c5a', '#7a5a2c'],
      amber:    ['#a8642c', '#5a2c14'],
    };
    const [a, b] = tones[tone] || tones.tortoise;
    el.style.background = `repeating-linear-gradient(135deg, rgba(0,0,0,0.05) 0 1px, transparent 1px 12px), linear-gradient(180deg, ${a}, ${b})`;
  });
}

/* ── Newsletter ──────────────────────────────────────────────── */
function initNewsletter() {
  const section = $('[data-newsletter-section]');
  if (!section) return;
  const form = $('form', section);
  if (!form) return;
  on(form, 'submit', e => {
    const email = $('[data-newsletter-email]', form);
    const btn = $('[data-newsletter-submit]', form);
    if (!email || !email.value.trim()) { e.preventDefault(); return; }
    if (btn) { btn.disabled = true; btn.textContent = 'Subscribing…'; }
    // Let Shopify handle the native form POST to /contact
  });
}

/* ── Announcement bar rotator ────────────────────────────────── */
const DGT_ANNOUNCEMENT_ANIM_MS = 500;

function dgtCreateAnnouncementMessageNode(item) {
  if (item.link) {
    const a = document.createElement('a');
    a.href = item.link;
    a.className = 'announcement-bar__message';
    a.textContent = item.text;
    return a;
  }
  const p = document.createElement('p');
  p.className = 'announcement-bar__message';
  p.textContent = item.text;
  return p;
}

function dgtCreateAnnouncementSlide(item, extraClass) {
  const slide = document.createElement('div');
  slide.className = `announcement-bar__slide ${extraClass || ''}`.trim();

  const clip = document.createElement('div');
  clip.className = 'announcement-bar__slide-clip';
  clip.appendChild(dgtCreateAnnouncementMessageNode(item));
  slide.appendChild(clip);
  return slide;
}

function dgtPruneAnnouncementSlides(track, keep) {
  track.querySelectorAll('.announcement-bar__slide').forEach(slide => {
    if (slide !== keep) slide.remove();
  });
}

function dgtDestroyAnnouncementRotator(bar) {
  if (bar._dgtAnnouncementCleanup) {
    bar._dgtAnnouncementCleanup();
    bar._dgtAnnouncementCleanup = null;
  }
  bar.dataset.announcementRotatorInit = '';
}

function dgtInitAnnouncementRotator(bar) {
  if (bar.dataset.announcementRotatorInit === '1') return;

  const dataRoot = bar.querySelector('[data-announcement-data]');
  const track = bar.querySelector('[data-announcement-track]');
  if (!dataRoot || !track) return;

  let items = [];
  try {
    items = JSON.parse(dataRoot.textContent || '[]');
  } catch {
    items = [];
  }
  items = items.filter(item => item && item.text);
  if (items.length <= 1) return;

  bar.dataset.announcementRotatorInit = '1';
  dataRoot.remove();

  let index = 0;
  let timer = null;
  let finishTimer = null;
  let isAnimating = false;
  const intervalMs = (parseInt(bar.dataset.interval, 10) || 5) * 1000;
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  function getActiveSlide() {
    return track.querySelector('.announcement-bar__slide--active');
  }

  function finishTransition(incoming) {
    dgtPruneAnnouncementSlides(track, incoming);
    incoming.classList.remove('announcement-bar__slide--enter');
    incoming.classList.add('announcement-bar__slide--initial');
    isAnimating = false;
    finishTimer = null;
  }

  function showAt(nextIndex) {
    if (isAnimating || nextIndex === index) return;
    isAnimating = true;
    window.clearTimeout(finishTimer);

    const outgoing = getActiveSlide();

    if (reducedMotion) {
      if (outgoing) outgoing.remove();
      const slide = dgtCreateAnnouncementSlide(items[nextIndex], 'announcement-bar__slide--active announcement-bar__slide--initial');
      track.appendChild(slide);
      dgtPruneAnnouncementSlides(track, slide);
      index = nextIndex;
      isAnimating = false;
      return;
    }

    const incoming = dgtCreateAnnouncementSlide(items[nextIndex], 'announcement-bar__slide--enter');
    track.appendChild(incoming);
    void incoming.offsetHeight;

    requestAnimationFrame(() => {
      if (outgoing) {
        outgoing.classList.remove('announcement-bar__slide--initial', 'announcement-bar__slide--active');
        outgoing.classList.add('announcement-bar__slide--exit');
      }
      incoming.classList.add('announcement-bar__slide--active');
    });

    finishTimer = window.setTimeout(() => {
      if (outgoing) outgoing.remove();
      index = nextIndex;
      finishTransition(incoming);
    }, DGT_ANNOUNCEMENT_ANIM_MS);
  }

  function next() {
    showAt((index + 1) % items.length);
  }

  function prev() {
    showAt((index - 1 + items.length) % items.length);
  }

  function resetTimer() {
    window.clearInterval(timer);
    timer = window.setInterval(next, intervalMs);
  }

  function pauseTimer() {
    window.clearInterval(timer);
    timer = null;
  }

  const initial = getActiveSlide();
  if (initial) {
    initial.classList.add('announcement-bar__slide--initial');
    dgtPruneAnnouncementSlides(track, initial);
  }

  resetTimer();

  const nextBtn = bar.querySelector('[data-announcement-next]');
  const prevBtn = bar.querySelector('[data-announcement-prev]');
  const onNext = () => { next(); resetTimer(); };
  const onPrev = () => { prev(); resetTimer(); };
  const onEnter = () => pauseTimer();
  const onLeave = () => resetTimer();

  if (nextBtn) on(nextBtn, 'click', onNext);
  if (prevBtn) on(prevBtn, 'click', onPrev);
  bar.addEventListener('mouseenter', onEnter);
  bar.addEventListener('mouseleave', onLeave);

  bar._dgtAnnouncementCleanup = () => {
    pauseTimer();
    window.clearTimeout(finishTimer);
    if (nextBtn) off(nextBtn, 'click', onNext);
    if (prevBtn) off(prevBtn, 'click', onPrev);
    bar.removeEventListener('mouseenter', onEnter);
    bar.removeEventListener('mouseleave', onLeave);
  };
}

function initAnnouncementBar() {
  $$('[data-announcement-bar]').forEach(bar => dgtInitAnnouncementRotator(bar));
}

document.addEventListener('shopify:section:load', e => {
  const bar = e.target.querySelector?.('[data-announcement-bar]');
  if (bar) {
    dgtDestroyAnnouncementRotator(bar);
    dgtInitAnnouncementRotator(bar);
  }
});

document.addEventListener('shopify:section:unload', e => {
  const bar = e.target.querySelector?.('[data-announcement-bar]');
  if (bar) dgtDestroyAnnouncementRotator(bar);
});

/* ── Wishlist ─────────────────────────────────────────────────── */
class Wishlist {
  constructor() {
    this.KEY = 'dgt_wishlist';
    this.items = this._load();
    this.syncButtons();
    this._bindAll();
  }

  _load() {
    try { return JSON.parse(localStorage.getItem(this.KEY) || '[]'); } catch { return []; }
  }

  _save() {
    localStorage.setItem(this.KEY, JSON.stringify(this.items));
  }

  has(id) {
    return this.items.some(i => i.id === id);
  }

  toggle(id, data) {
    if (this.has(id)) {
      this.items = this.items.filter(i => i.id !== id);
    } else {
      this.items.push({ id, ...data });
    }
    this._save();
    this.syncButtons();
    return this.has(id);
  }

  syncButtons() {
    $$('[data-wishlist-btn]').forEach(btn => {
      const id = parseInt(btn.dataset.wishlistBtn);
      const saved = this.has(id);
      btn.classList.toggle('is-saved', saved);
      btn.setAttribute('aria-label', saved ? 'Remove from wishlist' : 'Add to wishlist');
      const svg = btn.querySelector('svg path');
      if (svg) svg.style.fill = saved ? 'currentColor' : 'none';
    });

    // Update wishlist count in header if badge exists
    $$('[data-wishlist-count]').forEach(el => {
      el.textContent = this.items.length;
      el.classList.toggle('has-items', this.items.length > 0);
    });
  }

  _bindAll() {
    on(document, 'click', e => {
      const btn = e.target.closest('[data-wishlist-btn]');
      if (!btn) return;
      e.preventDefault();

      // Login gate — redirect to login if customer is not logged in
      if (btn.dataset.wishlistLogin) {
        window.location.href = btn.dataset.wishlistLogin;
        return;
      }

      const id = parseInt(btn.dataset.wishlistBtn);
      const card = btn.closest('[data-variant-id]') || btn.closest('.product-card');
      const data = {
        title: card?.querySelector('.product-card-title')?.textContent?.trim() || btn.dataset.wishlistTitle || '',
        vendor: card?.querySelector('.product-card-brand')?.textContent?.trim() || btn.dataset.wishlistVendor || '',
        price: card?.querySelector('.product-card-price')?.textContent?.trim() || btn.dataset.wishlistPrice || '',
        image: card?.querySelector('img')?.src || btn.dataset.wishlistImage || '',
        url: card?.querySelector('a')?.href || btn.dataset.wishlistUrl || '',
      };
      const isSaved = this.toggle(id, data);
      // Brief visual feedback
      btn.classList.add('wishlist-flash');
      setTimeout(() => btn.classList.remove('wishlist-flash'), 400);
    });
  }
}

/* ── Quick View ───────────────────────────────────────────────── */
function initQuickView() {
  const overlay = document.getElementById('QuickViewOverlay');
  const body    = document.getElementById('QuickViewBody');
  if (!overlay || !body) return;

  function closeQV() {
    overlay.classList.remove('open');
    document.body.style.overflow = '';
  }

  function fmtMoney(cents) {
    return '₹' + (cents / 100).toLocaleString('en-IN');
  }

  function renderQV(p) {
    const first  = p.variants[0];
    const images = p.images.slice(0, 6);

    const thumbsHTML = images.length > 1 ? images.map((img, i) =>
      `<button class="quick-view-thumb${i === 0 ? ' active' : ''}" data-qv-thumb data-src="${img}">
        <img src="${img}" alt="" loading="lazy">
      </button>`
    ).join('') : '';

    let optionsHTML = '';
    p.options.forEach((opt, oi) => {
      if (opt.values.length === 1 && opt.values[0] === 'Default Title') return;
      const seen = new Set();
      const swatches = p.variants.filter(v => {
        const val = v[`option${oi + 1}`];
        if (seen.has(val)) return false;
        seen.add(val);
        return true;
      }).map(v => {
        const val = v[`option${oi + 1}`];
        const imgSrc = v.featured_image ? v.featured_image.src : '';
        return `<button
          class="quick-view-swatch${v.id === first.id ? ' active' : ''}"
          data-qv-swatch
          data-variant-id="${v.id}"
          data-price="${v.price}"
          data-image="${imgSrc}"
          data-available="${v.available}"
          title="${val}"
          style="background:#ccc;"
        ></button>`;
      }).join('');
      optionsHTML += `<div>
        <div class="quick-view-option-label">${opt.name}</div>
        <div class="quick-view-swatches">${swatches}</div>
      </div>`;
    });

    body.innerHTML = `
      <div class="quick-view-grid">
        <div class="quick-view-media">
          <div class="quick-view-img-box">
            <img src="${images[0] || ''}" alt="${p.title}" id="QVMainImg" loading="eager">
          </div>
          ${images.length > 1 ? `<div class="quick-view-thumbs">${thumbsHTML}</div>` : ''}
        </div>
        <div class="quick-view-info">
          <div class="quick-view-vendor">${p.brand || p.vendor || ''}</div>
          <h2 class="quick-view-title">${p.title}</h2>
          <div class="quick-view-price" id="QVPrice"${Number(first.price) === 0 ? ' style="display:none"' : ''}>${Number(first.price) === 0 ? '' : fmtMoney(first.price)}</div>
          ${optionsHTML}
          <div class="quick-view-actions">
            <button class="quick-view-add" id="QVAdd" data-variant-id="${first.id}"${!first.available ? ' disabled' : ''}>
              ${first.available ? 'Add to Bag' : 'Sold Out'}
            </button>
            <div class="quick-view-link"><a href="/products/${p.handle}">View full details →</a></div>
          </div>
        </div>
      </div>`;

    // Thumb switching
    $$('[data-qv-thumb]', body).forEach(t => {
      on(t, 'click', () => {
        $$('[data-qv-thumb]', body).forEach(x => x.classList.remove('active'));
        t.classList.add('active');
        const img = document.getElementById('QVMainImg');
        if (img) img.src = t.dataset.src;
      });
    });

    // Swatch switching
    $$('[data-qv-swatch]', body).forEach(sw => {
      on(sw, 'click', () => {
        $$('[data-qv-swatch]', body).forEach(x => x.classList.remove('active'));
        sw.classList.add('active');
        const priceEl = document.getElementById('QVPrice');
        if (priceEl) {
          const swPrice = parseInt(sw.dataset.price, 10);
          if (swPrice === 0) {
            priceEl.textContent = '';
            priceEl.style.display = 'none';
          } else {
            priceEl.textContent = fmtMoney(swPrice);
            priceEl.style.display = '';
          }
        }
        const addBtn = document.getElementById('QVAdd');
        if (addBtn) {
          const avail = sw.dataset.available === 'true';
          addBtn.dataset.variantId = sw.dataset.variantId;
          addBtn.disabled = !avail;
          addBtn.textContent = avail ? 'Add to Bag' : 'Sold Out';
        }
        if (sw.dataset.image) {
          const img = document.getElementById('QVMainImg');
          if (img) img.src = sw.dataset.image;
        }
      });
    });

    // Add to cart
    const addBtn = document.getElementById('QVAdd');
    if (addBtn) {
      on(addBtn, 'click', async () => {
        const variantId = parseInt(addBtn.dataset.variantId);
        if (!variantId || addBtn.disabled) return;
        addBtn.disabled = true;
        addBtn.textContent = 'Adding…';
        try {
          const brand = p.brand || '';
          if (p.handle && brand) dgtBrandCache.set(p.handle, brand);
          await addToCart(variantId, 1, brand ? { _brand: brand } : undefined);
          addBtn.textContent = 'Added ✓';
          if (window.dgtCart) window.dgtCart.open();
          setTimeout(() => closeQV(), 900);
        } catch {
          addBtn.disabled = false;
          addBtn.textContent = 'Add to Bag';
        }
      });
    }
  }

  // Open on button click
  on(document, 'click', async e => {
    const btn = e.target.closest('[data-quick-view]');
    if (!btn) return;
    e.preventDefault();
    e.stopPropagation();
    const handle = btn.dataset.quickView;
    const card = btn.closest('.product-card');
    const cardBrand = card?.querySelector('.product-card-brand')?.textContent?.trim() || '';
    overlay.classList.add('open');
    document.body.style.overflow = 'hidden';
    body.innerHTML = '<div class="quick-view-loading"><span class="mono" style="font-size:10px;letter-spacing:0.14em;text-transform:uppercase;color:var(--ink-3);">Loading…</span></div>';
    try {
      const res = await fetch(`/products/${handle}.js`);
      const product = await res.json();
      if (cardBrand) product.brand = cardBrand;
      renderQV(product);
    } catch {
      body.innerHTML = '<div class="quick-view-loading"><span style="color:var(--ink-3);font-size:13px;">Could not load product.</span></div>';
    }
  });

  // Close
  on(document, 'click', e => {
    if (e.target.closest('[data-quick-view-close]')) closeQV();
  });
  on(document, 'keydown', e => {
    if (e.key === 'Escape') closeQV();
  });
}

/* ── Hero media hover (desktop parallax + label) ─────────────── */
function initHeroMediaHover() {
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const desktopHover = window.matchMedia('(hover: hover) and (pointer: fine) and (min-width: 901px)');

  $$('[data-hero-media]').forEach((media) => {
    const img = media.querySelector('[data-hero-img]');
    if (!img) return;

    let raf = 0;
    let active = false;
    let targetX = 0;
    let targetY = 0;
    let curX = 0;
    let curY = 0;
    const maxMove = 16;
    const baseScale = 1.06;

    const apply = () => {
      img.style.transform = `scale(${baseScale}) translate3d(${curX.toFixed(2)}px, ${curY.toFixed(2)}px, 0)`;
    };

    const reset = () => {
      img.style.transform = '';
    };

    const tick = () => {
      curX += (targetX - curX) * 0.1;
      curY += (targetY - curY) * 0.1;
      apply();
      if (
        active ||
        Math.abs(targetX - curX) > 0.05 ||
        Math.abs(targetY - curY) > 0.05
      ) {
        raf = requestAnimationFrame(tick);
      } else {
        raf = 0;
        if (!active) reset();
      }
    };

    const start = () => {
      if (!desktopHover.matches || reduceMotion.matches) return;
      active = true;
      media.classList.add('is-hover');
      if (!raf) raf = requestAnimationFrame(tick);
    };

    const move = (e) => {
      if (!active) return;
      const rect = media.getBoundingClientRect();
      const x = (e.clientX - rect.left) / rect.width - 0.5;
      const y = (e.clientY - rect.top) / rect.height - 0.5;
      targetX = x * maxMove * -1;
      targetY = y * maxMove * -1;
    };

    const end = () => {
      active = false;
      media.classList.remove('is-hover');
      targetX = 0;
      targetY = 0;
      if (!raf) raf = requestAnimationFrame(tick);
    };

    on(media, 'mouseenter', start);
    on(media, 'mousemove', move);
    on(media, 'mouseleave', end);
  });
}

/* ── Init ────────────────────────────────────────────────────── */
document.addEventListener('DOMContentLoaded', () => {
  initHeader();
  initMegaMenu();
  initMobileMenu();
  initReveal();
  initHeroMediaHover();
  initSearch();
  initTestimonials();
  initAnnouncementBar();
  initCollectionFilters();
  initCollectionDescriptionToggle();
  initInstagramTones();
  initNewsletter();
  initProductForm();
  initAddToCart();
  initQuickView();
  window.dgtCart = new CartDrawer();
  window.dgtWishlist = new Wishlist();
});
